import { HtmlParser } from '@calendly/html-parser'
import { I18n } from '@calendly/i18n'
import { Icon } from '@calendly/ui/components/icon'
import { ArrowRecurringIcon, BlockIcon, CalendarBlockIcon, CalendarDateIcon, CalendarIcon } from '@calendly/ui/components/icons'
import { Item } from '@calendly/ui/components/menu'
import moment from 'moment-timezone'
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'

import { useBooleanState } from 'hooks/useBooleanState'
import { useViewportFit } from 'hooks/useViewportFit'

import { RemoveHolidayPopup } from '../remove_holiday_popup'
import { DatePopup } from './date_popup'
import { CalendarTable, DayContainer, Divider, Menu, StyledBody, StyledDayWithIcon, StyledHeader, Week, Weekday, Weekdays } from './styles'
import WeekdayPopup from './weekday_popup'

// i18n-tasks-use t("publisher.availability.schedules.selection_menu.edit_wday.#{wday}")
const EditRecurringDayMenuItem = ({ wday, editWeekday, useUpdatedStyles }) => (
  <Item onClick={() => editWeekday(wday)}>
    {useUpdatedStyles
      ? (
        <div>
          <ArrowRecurringIcon spacingRight="8" />
          {HtmlParser(I18n.t(`publisher.availability.schedules.selection_menu.edit_wday.${wday}`))}
        </div>
      ) : (
        <>
          <Icon name="recurring" />
          {HtmlParser(I18n.t(`publisher.availability.schedules.selection_menu.edit_wday.${wday}`))}
        </>
      )}
  </Item>
)

const EditDateOption = ({ useUpdatedStyles }) => (
  useUpdatedStyles ? (
    <div data-component="edit-dates-button">
      <CalendarDateIcon spacingRight="8" />
      {HtmlParser(I18n.t('publisher.event_type_editor.sections.available_hours.edit_date'))}
    </div>
  ) : (
    <>
      <CalendarIcon />
      {HtmlParser(I18n.t('publisher.availability.schedules.selection_menu.edit_all'))}
    </>
  )
)

const ResetDayMenuItem = ({ resetSelectedDays, cleanSelection }) => (
  <Item onClick={() => {
    resetSelectedDays()
    cleanSelection()
  }}
  >
    <Icon name="automated" />
    {I18n.t('publisher.availability.schedules.selection_menu.reset')}
  </Item>
)

const RestoreHolidayButton = ({ onClick }) => (
  <Item onClick={onClick}>
    <div>
      <BlockIcon spacingRight="8" />
      {HtmlParser(I18n.t('publisher.event_type_editor.sections.available_hours.selection_menu.restore_holiday'))}
    </div>
  </Item>
)

const RemoveHolidayButton = ({ onClick }) => (
  <Item onClick={onClick}>
    <div>
      <CalendarBlockIcon spacingRight="8" />
      {HtmlParser(I18n.t('publisher.event_type_editor.sections.available_hours.selection_menu.remove_holiday'))}
    </div>
  </Item>
)

const EditHolidayOption = ({ holiday, onAdd, onRemove }) => (
  holiday.enabled
    ? <RemoveHolidayButton onClick={() => onRemove({ holiday })} />
    : <RestoreHolidayButton onClick={() => { onAdd(holiday) }} />
)

const getSelectedDays = (weeks, startDate, endDate) => {
  if (!weeks || !startDate || !endDate) return []

  const days = weeks.flat()
  const startDateIndex = days.findIndex(({ id }) => id === startDate)
  const endDateIndex = days.findIndex(({ id }) => id === endDate)
  return days
    .filter((_, index) => index >= Math.min(startDateIndex, endDateIndex) && index <= Math.max(startDateIndex, endDateIndex))
    .map(({ id }) => id)
}

const EditAllOption = ({
  isRecurringDay,
  allowWdayUpdates,
  editWeekday,
  wday,
  cleanSelection,
  resetSelectedDays,
  useUpdatedStyles
}) => {
  if (isRecurringDay) {
    return (allowWdayUpdates && (
      <EditRecurringDayMenuItem
        useUpdatedStyles={useUpdatedStyles}
        editWeekday={editWeekday}
        wday={wday}
      />
    ))
  }

  return <ResetDayMenuItem cleanSelection={cleanSelection} resetSelectedDays={resetSelectedDays} />
}

const MenuItems = ({ holiday,
  onAddHoliday,
  onRemoveHoliday,
  openSpecificDatePopup,
  useUpdatedStyles,
  isRecurringDay,
  allowWdayUpdates,
  editWeekday,
  wday,
  cleanSelection,
  resetSelectedDays }) => (
    <>
      {(!holiday || !holiday?.enabled) && (
        <>
          <Item onClick={openSpecificDatePopup}>
            <EditDateOption useUpdatedStyles={useUpdatedStyles} />
          </Item>
          <EditAllOption
            isRecurringDay={isRecurringDay}
            allowWdayUpdates={allowWdayUpdates}
            editWeekday={editWeekday}
            wday={wday}
            cleanSelection={cleanSelection}
            resetSelectedDays={resetSelectedDays}
            useUpdatedStyles={useUpdatedStyles}
          />
          {holiday && <Divider />}
        </>
      )}
      {holiday && <EditHolidayOption holiday={holiday} onAdd={onAddHoliday} onRemove={onRemoveHoliday} />}
    </>
)

const Calendar = forwardRef(({ days, dates, weekdays, weeks,
  setWdayIntervals, setDates, setHolidays, setUserHolidaySet, timezone, activeFrom, activeTo, holidays = [], userHolidaySet = {},
  isFullHeight = false, activeWeekends = true, allowWdayUpdates = true, useUpdatedStyles = false, isOwnMember = null,
  readOnlyHolidays = false, isV4AvailabilityPage = false, horizontalSpacers = false, isEditorPreview = false }, ref) => {
  const { ref: conflictRef, rightConflict, bottomConflict } = useViewportFit({ bottom: 90 })
  const weekContainerRef = useRef()
  const [isWeekdayPopupOpen, openWeekdayPopup, closeWeekdayPopup] = useBooleanState(false)
  const [isSpecificDatePopupOpen, openSpecificDatePopup, closeSpecificDatePopup] = useBooleanState(false)
  const [holidayToRemove, setHolidayToRemove] = useState(null)
  const [selectedWeekday, setSelectedWeekday] = useState()
  const [isSelecting, startSelection, stopSelection] = useBooleanState(false)
  const [selectionStartDate, setSelectionStartDate] = useState()
  const [selectionEndDate, setSelectionEndDate] = useState()
  const selectedDays = getSelectedDays(weeks, selectionStartDate, selectionEndDate)

  useImperativeHandle(ref, () => ({
    openDatePopup() {
      openSpecificDatePopup()
    }
  }))

  const dateSpecificIds = dates.map(({ date: id }) => id)
  const isAnyPopupOpen = isWeekdayPopupOpen || isSpecificDatePopupOpen || holidayToRemove
  const isSpecificDaySelected = selectedDays.some((selectedDay) => dateSpecificIds.includes(selectedDay))

  const holidaysMap = holidays.reduce((acc, current) => {
    acc[current.date_observed] = current
    return acc
  }, {})

  const shouldShowMenu = (id) => {
    if (isAnyPopupOpen) return false
    if (isSelecting || selectedDays.length === 0) return false
    if (readOnlyHolidays && holidaysMap[id]?.enabled) return false
    if (selectedDays.length === 1) return id === selectionEndDate
    return isSpecificDaySelected && id === selectionEndDate
  }

  const cleanSelection = useCallback(() => {
    stopSelection()
    setSelectionStartDate(null)
    setSelectionEndDate(null)
  }, [stopSelection])

  const selectDate = (id, isBeforeToday) => {
    if (isBeforeToday) {
      cleanSelection()
      return
    }
    setSelectionStartDate(id)
    setSelectionEndDate(id)
  }

  const onSelectionStart = (id, isBeforeToday) => {
    if (isBeforeToday) {
      cleanSelection()
      return
    }
    startSelection()
    selectDate(id, isBeforeToday)
  }

  const onSelectionStop = () => {
    stopSelection()
    if (selectedDays.length > 1 && !isSpecificDaySelected) {
      openSpecificDatePopup()
    }
  }

  const onSelectionMove = (id, isBeforeToday) => {
    if (!isSelecting) return
    if (isBeforeToday) return
    setSelectionEndDate(id)
  }

  const resetSelectedDays = () => {
    setDates(dates.filter((rule) => !selectedDays.includes(rule.date)))
  }

  const datesMap = dates.reduce((acc, current) => {
    acc[current.date] = current
    return acc
  }, {})

  const editWeekday = (wday) => {
    setSelectedWeekday(wday)
    openWeekdayPopup()
  }

  const inActiveRange = (id) => {
    if (!activeFrom && !activeTo) {
      return true
    }

    const dayMoment = moment.tz(id, timezone)

    if (activeFrom && dayMoment.isBefore(activeFrom, 'day')) {
      return false
    }

    if (activeTo && dayMoment.isAfter(activeTo, 'day')) {
      return false
    }

    return true
  }

  const onAddHoliday = (holiday) => {
    setHolidays(holidays.map(
      (h) => (holiday.id === h.id ? { ...h, enabled: true } : { ...h })))
    cleanSelection()
  }

  const removeHoliday = ({ scope, holiday }) => {
    if (scope === 'local') {
      const key = isV4AvailabilityPage ? 'key' : 'id'
      setHolidays(holidays.map(
        (h) => (h[key] === holiday[key] ? { ...h, enabled: false } : { ...h })))
    } else {
      setUserHolidaySet({
        ...userHolidaySet,
        holidays: userHolidaySet.holidays.map((h) => (h.key === holiday.id ? { ...h, enabled: false } : { ...h }))
      })
      setHolidays(holidays.filter((h) => h.id !== holiday.id))
    }
    cleanSelection()
    setHolidayToRemove(null)
  }

  const onRemoveHoliday = isOwnMember
    ? ({ holiday }) => setHolidayToRemove(holiday)
    : ({ holiday }) => removeHoliday({ scope: 'local', holiday })

  const isRecurringDay = (id) => (selectedDays.length === 1 && !dateSpecificIds.includes(id))

  useEffect(() => {
    const onOutsideClick = (event) => {
      if (isAnyPopupOpen) return
      if (!weekContainerRef.current || weekContainerRef.current.contains(event.target)) return
      cleanSelection()
    }

    const onEscapeKeyPress = (event) => {
      if (isAnyPopupOpen) return
      if (event.key !== 'Escape') return
      cleanSelection()
    }

    // 'click' event triggers even when you start selecting few cells then moved mouse out of grid
    document.addEventListener('mousedown', onOutsideClick)
    document.addEventListener('keydown', onEscapeKeyPress)
    return () => {
      document.removeEventListener('mousedown', onOutsideClick)
      document.removeEventListener('keydown', onEscapeKeyPress)
    }
  }, [cleanSelection, isAnyPopupOpen, isSpecificDatePopupOpen, isWeekdayPopupOpen])

  return (
    <CalendarTable data-component="availability-calendar-view" isFullHeight={isFullHeight} isEditorPreview={isEditorPreview}>
      <StyledHeader>
        <Weekdays isEditorPreview={isEditorPreview} useUpdatedStyles={useUpdatedStyles}>
          {weekdays.map((weekday) => (
            <Weekday key={weekday} isEditorPreview={isEditorPreview} useUpdatedStyles={useUpdatedStyles}>{weekday}</Weekday>
          ))}
        </Weekdays>
      </StyledHeader>
      <StyledBody onMouseLeave={onSelectionStop} ref={weekContainerRef}>
        {weeks.map((week) => (
          <Week key={week[0].id} horizontalSpacers={horizontalSpacers}>
            {week.map(({ id, text, isToday, isBeforeToday, wday }) => (
              <DayContainer key={id} data-testid={`day-cell-${id}`} isEditorPreview={isEditorPreview}>
                <StyledDayWithIcon
                  aria-current={isToday ? 'date' : null}
                  id={id}
                  wday={wday}
                  datesMap={datesMap}
                  holidaysMap={holidaysMap}
                  days={days}
                  selectedDays={selectedDays}
                  isBeforeToday={isBeforeToday}
                  isSelecting={isSelecting}
                  selectDate={selectDate}
                  onSelectionStart={onSelectionStart}
                  onSelectionStop={onSelectionStop}
                  onSelectionMove={onSelectionMove}
                  text={text}
                  isToday={isToday}
                  activeWeekends={activeWeekends}
                  inActiveRange={inActiveRange}
                  isDropdownActive={shouldShowMenu(id)}
                  reduceToFit={isFullHeight}
                  isEditorPreview={isEditorPreview}
                />
                {shouldShowMenu(id) && (
                <Menu ref={conflictRef} pullLeft={rightConflict} pullUp={bottomConflict}>
                  <MenuItems
                    holiday={holidaysMap[id]}
                    onAddHoliday={onAddHoliday}
                    onRemoveHoliday={onRemoveHoliday}
                    openSpecificDatePopup={openSpecificDatePopup}
                    useUpdatedStyles={useUpdatedStyles}
                    isRecurringDay={isRecurringDay(id)}
                    allowWdayUpdates={allowWdayUpdates}
                    editWeekday={editWeekday}
                    wday={wday}
                    cleanSelection={cleanSelection}
                    resetSelectedDays={resetSelectedDays}
                  />
                </Menu>
                )}
              </DayContainer>
            ))}
          </Week>
        ))}
      </StyledBody>
      {isSpecificDatePopupOpen && (
        <DatePopup
          onClose={() => {
            cleanSelection()
            closeSpecificDatePopup()
          }}
          timezone={timezone}
          dates={dates}
          setDates={setDates}
          days={days}
          defaultDays={selectedDays}
        />
      )}
      {isWeekdayPopupOpen && allowWdayUpdates && (
        <WeekdayPopup
          day={selectedWeekday}
          intervals={days[selectedWeekday]}
          onClose={() => {
            cleanSelection()
            closeWeekdayPopup()
          }}
          setWdayIntervals={setWdayIntervals}
        />
      )}
      {holidayToRemove
        && (
          <RemoveHolidayPopup
            holiday={holidayToRemove}
            onConfirm={({ scope, holiday }) => { removeHoliday({ scope, holiday }) }}
            onClose={() => { setHolidayToRemove(null) }}
          />
        )}
    </CalendarTable>
  )
})

export default Calendar
